import type { ReactNode } from 'react';
import { PageContainer } from '../PageContainer/PageContainer';
import { TemplateSection } from './Section';

type Block = { title: ReactNode; content: ReactNode; action?: ReactNode };

export type CompanyProfileTemplateProps = {
  /** The BrandSurface hero: wide logo, name, context, GradeBadge pill (not on the hosted careers page), Follow with binoculars. */
  hero: ReactNode;
  /** The BadgeRail, attached under the hero. */
  badges?: ReactNode;
  /** Open jobs as bare JobCards (the page already is the employer). */
  jobs?: Block;
  /** The hiring Lifeline. */
  lifeline?: Block;
  /** StatTiles with sources. */
  stats?: ReactNode;
  /** The company Timeline. */
  timeline?: Block;
  /** Further sections (working there, money, details, similar employers as RankingRows). */
  more?: ReactNode;
  className?: string;
};

/**
 * The company page and the hosted careers page. Sparse companies show only the sections that have
 * data: every slot left out renders nothing, so honest "not known yet" states stay honest.
 */
export function CompanyProfileTemplate({
  hero,
  badges,
  jobs,
  lifeline,
  stats,
  timeline,
  more,
  className,
}: CompanyProfileTemplateProps) {
  return (
    <PageContainer width="wide" className={className}>
      <div className="flex flex-col gap-jc-section py-jc-block">
        <div className="flex flex-col gap-4">
          {hero}
          {badges}
        </div>
        {jobs ? (
          <TemplateSection id="jobs" title={jobs.title} action={jobs.action}>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{jobs.content}</div>
          </TemplateSection>
        ) : null}
        {/* The lifeline on the left; its figures and the company timeline stacked on the right, so the
            timeline fills the space under the figures instead of running half-width on its own below. */}
        {lifeline ? (
          <div className="grid gap-jc-section lg:grid-cols-2 lg:items-start">
            <TemplateSection id="hiring" title={lifeline.title}>
              {lifeline.content}
            </TemplateSection>
            {stats || timeline ? (
              <div className="flex flex-col gap-jc-section">
                {stats ? <div className="grid grid-cols-2 gap-3">{stats}</div> : null}
                {timeline ? (
                  <TemplateSection id="history" title={timeline.title}>
                    {timeline.content}
                  </TemplateSection>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {stats ? <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{stats}</div> : null}
            {timeline ? (
              <TemplateSection id="history" title={timeline.title} className="max-w-2xl">
                {timeline.content}
              </TemplateSection>
            ) : null}
          </>
        )}
        {more}
      </div>
    </PageContainer>
  );
}
